// graph-island: a context/ or patterns/ scaffold file that no frontmatter `edge`
// points to. Always-loaded anchors (ROUTER, AGENTS) are entry points, not islands,
// and are excluded. Deterministic; uses already-parsed frontmatter.

import { resolve } from "node:path";
import type { ParsedFile, Suggestion, SuggestContext, SuggestionProvider } from "../../types.js";

const ANCHORS = ["ROUTER.md", "AGENTS.md"];

function isAnchor(source: string): boolean {
  const base = source.split("/").pop() ?? source;
  return ANCHORS.includes(base);
}

/** Index/readme files are conventionally standalone, not navigation targets. */
const STANDALONE = ["INDEX.md", "README.md"];

/** Navigable scaffold files — the ones that should be reachable via edges. */
function isNavigable(source: string): boolean {
  const base = source.split("/").pop() ?? source;
  if (STANDALONE.includes(base)) return false;
  return source.includes("context/") || source.includes("patterns/");
}

export function findIslands(ctx: SuggestContext): Suggestion[] {
  // Resolve every edge target to an absolute path so we can match files.
  const reached = new Set<string>();
  for (const file of ctx.files) {
    for (const edge of file.frontmatter?.edges ?? []) {
      if (!edge.target) continue;
      reached.add(resolve(ctx.projectRoot, edge.target));
      reached.add(resolve(ctx.scaffoldRoot, edge.target));
    }
  }

  const suggestions: Suggestion[] = [];
  for (const file of ctx.files) {
    if (isAnchor(file.source) || !isNavigable(file.source)) continue;
    if (reached.has(file.path)) continue;

    suggestions.push({
      kind: "graph-island",
      title: `Unreachable scaffold file: ${file.source}`,
      rationale: `No frontmatter edge points to ${file.source}, so the agent has no navigation path to it. Add an edge from a relevant file, or remove it if obsolete.`,
      estimatedSaving: Math.max(1, file.lines.length), // its content is effectively dead weight
      files: [file.source],
    });
  }

  return suggestions;
}

export const graphIslandProvider: SuggestionProvider = {
  name: "graph-island",
  run: findIslands,
};
